import axios from 'axios';

/**
 * Study Center v2 — deliberately thin store.
 *
 * The legacy flow (Store/campuses.js → GET_CAMPUS_LESSONS_LIST) built
 * the vue-cal event list in the browser: free-slot generation per
 * teacher plus an O(teachers·slots·lessons) overlap scan. In v2 the
 * backend (DayGridService) returns ready events + paginated teacher
 * splits, so this module only stores the payload and loading state.
 */
export default {
  namespaced: true,

  state: {
    day: null, // GET /api/study-center/v2/day payload
    loading: false,
    filters: {
      date: null,
      search: '',
      category_url: 'study-center',
      class_id: [],
      group_id: [],
      column_id: [],
      institution_id: 0,
      board_id: 0,
      skill_id: 0,
      place_id: 0,
      type_id: 0
    }
  },

  getters: {
    teachers: state => (state.day ? state.day.teachers : []),
    events: state => (state.day ? state.day.events : []),
    bounds: state =>
      state.day ? state.day.bounds : { from: 480, to: 1080, step: 10 },
    meta: state => (state.day ? state.day.meta : null),
    blocked: state => (state.day ? state.day.blocked : false)
  },

  mutations: {
    SET_DAY(state, payload) {
      // Keep the previous teachers ARRAY REFERENCE when the set didn't
      // change: vue-cal watches :split-days, and a fresh (but identical)
      // array makes it rebuild all columns — visible as a flicker on
      // every reload. Fresh fields (lessons_count, skills) are copied
      // onto the existing objects in place so headers still update.
      if (state.day && payload) {
        const prev = state.day.teachers.map(t => t.id).join(',');
        const next = (payload.teachers || []).map(t => t.id).join(',');
        if (prev === next) {
          state.day.teachers.forEach((t, i) => {
            Object.assign(t, payload.teachers[i]);
          });
          payload.teachers = state.day.teachers;
        }
      }
      state.day = payload;
    },
    APPEND_PAGE(state, payload) {
      // Horizontal infinite scroll: merge the next teachers page.
      if (!state.day || !payload) {
        return;
      }
      const seen = new Set(state.day.teachers.map(t => t.id));
      state.day.teachers = state.day.teachers.concat(
        (payload.teachers || []).filter(t => !seen.has(t.id))
      );
      state.day.events = state.day.events.concat(payload.events || []);
      state.day.meta = payload.meta;
    },
    SET_LOADING(state, val) {
      state.loading = val;
    },
    SET_FILTERS(state, patch) {
      state.filters = { ...state.filters, ...patch };
    }
  },

  actions: {
    async fetchDay({ state, commit, rootState }, opts = {}) {
      const append = !!opts.append;
      if (opts.filters) {
        commit('SET_FILTERS', opts.filters);
      }
      const campusId =
        (rootState.settings &&
          rootState.settings.selectedCampus &&
          rootState.settings.selectedCampus.id) ||
        1;

      commit('SET_LOADING', true);
      try {
        const { data } = await axios.get('/api/study-center/v2/day', {
          params: {
            campus_id: campusId,
            category_url: state.filters.category_url,
            date: state.filters.date,
            search: state.filters.search || undefined,
            class_id:
              state.filters.class_id && state.filters.class_id.length
                ? state.filters.class_id
                : undefined,
            group_id:
              state.filters.group_id && state.filters.group_id.length
                ? state.filters.group_id
                : undefined,
            column_id:
              state.filters.column_id && state.filters.column_id.length
                ? state.filters.column_id
                : undefined,
            institution_id: state.filters.institution_id || 0,
            board_id: state.filters.board_id || undefined,
            skill_id: state.filters.skill_id || undefined,
            place_id: state.filters.place_id || undefined,
            type_id: state.filters.type_id || undefined,
            page: append && state.day ? state.day.meta.current_page + 1 : 1
          }
        });
        commit(append ? 'APPEND_PAGE' : 'SET_DAY', data.data);
      } finally {
        commit('SET_LOADING', false);
      }
    }
  }
};
